import React, { useEffect, useState } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import searchIcon from '../../../../../_metronic/assets/images/svg/data-room/search-icon-grey.svg'
import { useAuth } from '../../../auth'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'
import NoDataPage from '../../../widgets/components/NoDataPage'
import '../../../stakeholders/style/stakeholders.scss'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DisplayImage } from '../../../widgets/components/General/DisplayImage'
import { CSVBoxButton } from '@csvbox/react'
import { useStakeholders } from '../../Stakeholders'
import clsx from 'clsx'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { Filter } from '../../../widgets/components/General/Filter'
import { DeleteFounderPromoterModal } from '../../founders-and-promoters/components/DeleteFounderPromoterModal'
import {
  deleteTeamMember,
  exportTeamManagement,
  getTeamManagement,
  importTeamManagement,
} from '../core/_requests'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../../hooks/gtmHelpers'
import { useThemeMode } from '../../../../../_metronic/partials'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

interface TeamMember {
  memberId: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string | null
  title: string | null
  profileImage: {
    id: number
    name: string
  } | null
}

export const DataTableServerSide = ({
  getDashboardCounts,
}: {
  getDashboardCounts: () => Promise<void>
}) => {
  const { currentUser } = useAuth()
  const { mode } = useThemeMode()
  const [loading, setLoading] = useState<boolean>(false)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const [data, setData] = useState<TeamMember[]>([])
  const [showDeleteModal, setDeleteShowModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [memberId, setmemberId] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })
  const { companyId } = useAuth()
  const { setTeamManagementFilter, selectedTeamManagementTypeFilter } = useStakeholders()

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        cell: (info) => {
          const row = info.row.original
          const fullName = row.firstName && row.lastName ? `${row.firstName} ${row.lastName}` : '-'
          return (
            <Link to={`/stakeholders/team-member-detail/${row.memberId}`}>
              <DisplayImage
                imgName={row?.profileImage?.name}
                height='20px'
                width='20px'
                fit='contain'
                alt='profile'
                className='rounded-circle me-2'
              />
              <span className='ms-2 text-dark'>{fullName}</span>
            </Link>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'title',
        id: 'Type',
        header: 'Title',
        cell: (info) => {
          const row = info.row.original

          return (
            <Link to={`/stakeholders/team-member-detail/${row.memberId}`}>
              <div className={`px-3 text-dark`}>{row.title || '-'}</div>
            </Link>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'email',
        id: 'email',
        header: 'Email',
        cell: (info) => {
          const row = info.row.original
          return (
            <Link to={`/stakeholders/team-member-detail/${row.memberId}`}>
              <div className='text-dark'>{info.getValue() ?? '-'}</div>
            </Link>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'phoneNumber',
        id: 'phone',
        header: 'Phone',
        cell: (info) => {
          const row = info.row.original
          return (
            <Link to={`/stakeholders/team-member-detail/${row.memberId}`}>
              <div className='text-dark'>{info.getValue() ?? '-'}</div>
            </Link>
          )
        },
        filterFn: 'includesString',
      },
      // {
      //   id: 'actions',
      //   header: 'Actions',
      //   cell: ({ row }) => {
      //     return (
      //       <div className='d-flex justify-content-between ms-4 dropdown-container'>
      //         <Dropdown
      //           show={selectedIndex === row.index}
      //           onToggle={(isOpen) => {
      //             setSelectedIndex(isOpen ? row.index : undefined)
      //           }}
      //         >
      //           <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
      //             <img src={threeDots} alt='three-dots' className='three-dots-image' />
      //           </Dropdown.Toggle>
      //           <Dropdown.Menu className='m-2 '>
      //             <Dropdown.Item className='text-primary ps-5'>
      //               <Link to={`/stakeholders/team-member-detail/${row?.original?.memberId}`}>
      //                 {formatMessage({ id: 'Preview' })}
      //               </Link>
      //             </Dropdown.Item>

      //             <Dropdown.Item
      //               className='text-dark ps-5'
      //               onClick={() => {
      //                 navigate(
      //                   `/stakeholders/add-team-member?memberId=${row?.original?.memberId}&edit=true`
      //                 )
      //               }}
      //             >
      //               {formatMessage({ id: 'Edit' })}
      //             </Dropdown.Item>

      //             <Dropdown.Item
      //               className='text-danger ps-5'
      //               onClick={() => {
      //                 setmemberId(row?.original?.memberId)
      //                 setDeleteShowModal(true)
      //               }}
      //             >
      //               {formatMessage({ id: 'Delete' })}
      //             </Dropdown.Item>
      //           </Dropdown.Menu>
      //         </Dropdown>
      //       </div>
      //     )
      //   },
      // },
    ],
    [selectedIndex] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    // Check if the search term is in the email or the combined name fields
    const emailMatch = rankItem(row.original.email, value)
    const nameMatch = rankItem(`${row.original.firstName} ${row.original.lastName}`, value)

    // Determine which match to use based on rank comparison or other logic
    let bestMatch: RankingInfo | undefined
    if (emailMatch.passed && nameMatch.passed) {
      bestMatch = emailMatch.rank < nameMatch.rank ? emailMatch : nameMatch
    } else if (emailMatch.passed) {
      bestMatch = emailMatch
    } else if (nameMatch.passed) {
      bestMatch = nameMatch
    }

    // Only call addMeta if a best match is found
    if (bestMatch) {
      addMeta({ itemRank: bestMatch })
    }

    // Return whether the search term passed in either field
    return !!bestMatch
  }

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const fetchTeamManagement = async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const response = await getTeamManagement(
        companyId,
        pagination.currentPage,
        pagination.pageSize,
        selectedTeamManagementTypeFilter
      )
      if (response.data.success) {
        setPagination((prev) => ({
          ...prev,
          currentPage: response.data.data.pagination.currentPage,
          pageSize: response.data.data.pagination.pageSize,
          totalCount: response.data.data.pagination.totalRecords,
          nextPage: response.data.data.pagination.nextPage,
          prevPage: response.data.data.pagination.prevPage,
          totalPages: response.data.data.pagination.totalPages,
        }))

        setData(response.data?.data?.managementTeams)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.TeamMemberAccessSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              management_team: response.data.data.managementTeams,
            },
          },
        })
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.TeamMemberAccessFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              error: response.data.errors[0],
            },
          },
        })
      }
    } catch (error) {
      console.error('Error fetching founders/promoters:', error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.TeamMemberAccessFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            error: error,
          },
        },
      })
    } finally {
      setLoading(false)
    }
  }

  const importedFileUpload = async (teamMemberData: any) => {
    try {
      const cleanedTeamMemberData = teamMemberData?.map((teamMember: any) => {
        const cleanedteamMember = { ...teamMember }
        Object.keys(cleanedteamMember).forEach((key) => {
          if (cleanedteamMember[key] === '') {
            delete cleanedteamMember[key]
          }
        })
        return cleanedteamMember
      })

      const {
        data: { success },
      } = await importTeamManagement(cleanedTeamMemberData, companyId!)
      if (success) {
        toast.success(formatMessage({ id: 'CSV imported successfully' }))
        await fetchTeamManagement()
        await getDashboardCounts()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ImportTeamManagementSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
            },
          },
        })
      }
    } catch (error) {
      console.error('Error importing founders promoters:', error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.ImportTeamManagementFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            error: error,
          },
        },
      })
    }
  }

  const onExportTeamMembers = async () => {
    try {
      const {
        data: { data: values, success, errors },
      } = await exportTeamManagement(companyId!)
      if (success) {
        toast.success(formatMessage({ id: 'Documents exported successfully' }))
        const url = window.URL.createObjectURL(new Blob([values]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'team_management.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ExportTeamManagementSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              management_team: values,
            },
          },
        })
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ExportTeamManagementFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              error: errors[0],
            },
          },
        })
      }
    } catch (err: any) {
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.ExportTeamManagementFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            error: err,
          },
        },
      })
    }
  }

  const handleDeleteTeamMember = async () => {
    if (!companyId) return
    setDeleteLoading(true)
    try {
      const {
        data: { success },
      } = await deleteTeamMember(memberId!, companyId)
      if (success) {
        toast.success(formatMessage({ id: 'Team member deleted successfully' }))
        await fetchTeamManagement()
        await getDashboardCounts()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DeleteTeamMemberSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              member_id: memberId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
            },
          },
        })
      }
    } catch (error) {
      console.log(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DeleteTeamMemberFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            member_id: memberId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            error: error,
          },
        },
      })
    } finally {
      setDeleteLoading(false)
      setDeleteShowModal(false)
    }
  }

  useEffect(() => {
    fetchTeamManagement()
    // eslint-disable-next-line
  }, [companyId, pagination.currentPage, pagination.pageSize, selectedTeamManagementTypeFilter])

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  return (
    <>
      <div className='d-md-flex justify-content-between align-items-center my-4'>
        <h1 className='fs-4 font-size-20'>{formatMessage({ id: 'Team Members' })}</h1>
        <div className='d-flex gap-2 align-items-center justify-content-end mb-4'>
          <div className='h-100 bg-white d-flex rounded height-36 align-items-center'>
            <button
              className={clsx('text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36')}
              id='fc-toogle'
              title='Sort and Filter'
              data-bs-toggle='tooltip'
              data-bs-placement='left'
              data-bs-dismiss='click'
              data-bs-trigger='hover'
              onClick={() => {
                setTeamManagementFilter(true)
                if (selectedTeamManagementTypeFilter) {

                  TagManager.dataLayer({
                    dataLayer: {
                      event: GTMEvent.TeamManagementFilterRemovedSuccessful,
                      action: 'normal',
                      metadata: {
                        company_id: companyId,
                        user_id: currentUser?.userId,
                        email: currentUser?.email,
                      },
                    },
                  })
                } else {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: GTMEvent.TeamManagementFilterAppliedSuccessful,
                      action: 'normal',
                      metadata: {
                        company_id: companyId,
                        user_id: currentUser?.userId,
                        email: currentUser?.email,
                        team_management_type: selectedTeamManagementTypeFilter,
                      },
                    },
                  })
                }
              }}
            >
              <KTSVG
                path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                className='svg-icon-6 svg-icon-muted me-1'
              />
              <span className='d-none d-md-block'>{formatMessage({ id: 'Filter' })}</span>
              {selectedTeamManagementTypeFilter ? '*' : ''}
            </button>
          </div>

          <form className='align-items-center d-flex height-36 w-100'>
            <span className='svg-icon svg-icon-3 position-absolute ms-2'>
              <img src={searchIcon} alt='Search icon' />
            </span>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => {
                setGlobalFilter(String(value))
                if (String(value).length > 0) {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: GTMEvent.TeamManagementSearchSuccessful,
                      action: 'normal',
                      metadata: {
                        company_id: companyId,
                        user_id: currentUser?.userId,
                        email: currentUser?.email,
                        search_value: value,
                      },
                    },
                  })
                }
              }}
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-md-12 font-size-13 font-weight-400 height-36'
              placeholder='Search by name or email'
            />
          </form>
          <div className='position-relative'>
            <CSVBoxButton
              licenseKey={process.env.REACT_APP_CSVBOX_LICENSE_KEY_TEAM_MANAGEMENT}
              user={{
                user_id: `${currentUser?.userId}`,
              }}
              onImport={(result: any, data: any) => {
                if (result) {
                  importedFileUpload(data.rows)
                } else {
                  toast.error(formatMessage({ id: 'File import failed, Please try later' }))
                }
              }}
              render={(launch: any) => {
                return (
                  <div className='dropdown-export-container'>
                    <Dropdown className='height-36'>
                      <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                        <img src={threeDots} alt='' className='three-dots-image' />
                      </Dropdown.Toggle>

                      <Dropdown.Menu className='m-1'>
                        <Dropdown.Item className='text-dark ps-5 border' onClick={launch}>
                          {formatMessage({ id: 'Import CSV' })}
                        </Dropdown.Item>
                        <Dropdown.Item className='text-dark ps-5' onClick={onExportTeamMembers}>
                          {formatMessage({ id: 'Export to CSV' })}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                )
              }}
            ></CSVBoxButton>
          </div>
        </div>
      </div>

      <div className='bg-white rounded-2 p-4'>
        {!loading && (data?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage width={'w-0'} />
          </div>
        ) : (
          <div className="overflow-x-auto hide-scrollbar">
            <Table className='w-100 data-table-link-color'>
              <thead className='custom-thead border-bottom'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className='font-size-12 font-weight-400 w-100 table-row border-dark-mode '
                  >
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} colSpan={header.colSpan} className=' pb-6 pt-4 table-column-width'>
                        {header.isPlaceholder ? null : (
                          <div
                            className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                    <th className='min-w-120px pb-6 pt-4'>{formatMessage({ id: 'Actions' })}</th>
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <>
                    <SkeletonRow count={8} />
                  </>
                ) : (
                  table.getRowModel().rows?.map((row) => (
                    <tr key={row.id} className='border-bottom'>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className='pt-4 border-0'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                      <td>
                        <div className='d-flex justify-content-between ms-4 dropdown-container'>
                          <Dropdown
                            show={selectedIndex === row.index}
                            onToggle={(isOpen) => {
                              setSelectedIndex(isOpen ? row.index : undefined)
                            }}
                          >
                            <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                              <img src={threeDots} alt='three-dots' className='three-dots-image' />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className='m-2 '>
                              <Dropdown.Item className='text-primary ps-5'>
                                <Link to={`/stakeholders/team-member-detail/${row?.original?.memberId}`}>
                                  {formatMessage({ id: 'Preview' })}
                                </Link>
                              </Dropdown.Item>

                              <Dropdown.Item
                                className='text-dark ps-5'
                                onClick={() => {
                                  navigate(
                                    `/stakeholders/add-team-member?memberId=${row?.original?.memberId}&edit=true`
                                  )
                                }}
                              >
                                {formatMessage({ id: 'Edit' })}
                              </Dropdown.Item>

                              <Dropdown.Item
                                className='text-danger ps-5'
                                onClick={() => {
                                  setmemberId(row?.original?.memberId)
                                  setDeleteShowModal(true)
                                }}
                              >
                                {formatMessage({ id: 'Delete' })}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        {!loading && data?.length > 0 && pagination.totalCount > 10 && (
          <div className='w-100 d-md-flex justify-content-between align-items-center px-0 mt-8'>
            <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4'>
              <select
                aria-label='Default select example'
                className={`p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark ${mode === 'dark' ? ' border-dark' : ''}`}
                value={pagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value)
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: newSize,
                    pageIndex: 0, // Reset to first page when page size changes
                  }))
                }}
              >
                {[10, 20, 50, 100].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>

              <span className='font-size-13 text-center text-md-start'>
                {formatMessage({
                  id: `Showing ${pagination.currentPage === 1
                    ? 1
                    : (pagination.currentPage - 1) * pagination.pageSize + 1
                    } to ${pagination.nextPage ? data.length : pagination.totalCount} of ${pagination.totalCount
                    } records`,
                })}
              </span>
            </div>

            {/* <Pagination className='w-md-100 w-auto d-flex flex-md-end justify-content-center justify-content-md-end '>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === pagination.currentPage - 1}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination> */}
            <Pagination className='w-md-100 w-auto d-flex flex-md-end justify-content-center justify-content-md-end '>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {(() => {
                const items = [];
                const totalPages = pagination.totalPages;
                const current = pagination.currentPage;
                const pageNeighbours = 1; // Number of pages to show on each side

                const startPage = Math.max(2, current - pageNeighbours);
                const endPage = Math.min(totalPages - 1, current + pageNeighbours);

                // Always show first page
                items.push(
                  <Pagination.Item
                    key={1}
                    active={current === 1}
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </Pagination.Item>
                );

                // Show start ellipsis
                if (startPage > 2) {
                  items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
                }

                // Middle pages
                for (let i = startPage; i <= endPage; i++) {
                  items.push(
                    <Pagination.Item
                      key={i}
                      active={i === current}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </Pagination.Item>
                  );
                }

                // Show end ellipsis
                if (endPage < totalPages - 1) {
                  items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
                }

                // Always show last page
                if (totalPages > 1) {
                  items.push(
                    <Pagination.Item
                      key={totalPages}
                      active={current === totalPages}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </Pagination.Item>
                  );
                }

                return items;
              })()}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination>

          </div>
        )}
      </div>
      <DeleteFounderPromoterModal
        setDeleteShowModal={setDeleteShowModal}
        showDeleteModal={showDeleteModal}
        onDeleteSubmit={handleDeleteTeamMember}
        deleteLoading={deleteLoading}
        header='Delete Team Member'
        paragraph='Are you sure you want to delete this team member?'
      />
      <Filter />
    </>
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
